/**
 * DatabaseRepository module for persisting and retrieving game entries using PocketBase
 * Web UI version - uses PocketBase SDK instead of in-memory storage
 * Requirements: 10.1, 10.2, 10.3, 10.4
 */

import { GameEntry } from '../models/GameEntry.js';

/**
 * DatabaseRepository class - manages game entry persistence with PocketBase
 */
class DatabaseRepository {
    /**
     * Creates a new DatabaseRepository
     * @param {PocketBase} pocketbase - PocketBase instance
     * @param {import('@code-burrow/design-system').ProxyClient} proxy - ProxyClient for write operations
     */
    constructor(pocketbase, proxy) {
        this.pb = pocketbase;
        this.proxy = proxy;
        this.collection = 'videogames';
    }

    /**
     * Saves a game entry to the database.
     * @param {GameEntry} entry - The game entry to save
     * @returns {Promise<Object>} - Result object with success boolean and optional error
     * Requirements: 10.1, 10.2
     */
    async saveGameEntry(entry) {
        try {
            const toISO = (val) => {
                if (!val) return null;
                if (typeof val === 'string') return val;
                return val.toISOString();
            };
            const data = {
                steamID: entry.steamID,
                source: entry.source || 'steam',
                title: entry.title,
                imageURL: entry.imageURL,
                genres: entry.genres,
                storeURL: entry.storeURL,
                creator: entry.creator,
                releaseDate: toISO(entry.releaseDate),
                isDLC: entry.isDLC,
            };

            const record = await this.proxy.collection(this.collection).create(data);

            return { success: true, record, pbId: record.id };
        } catch (error) {
            // Handle unique constraint violation (duplicate steamID) —
            // race condition where another user added the same game concurrently.
            // Return the existing record so the caller can still create user_games.
            if (error.status === 400 && error.data?.data?.steamID) {
                const existing = await this.getGameEntry(entry.steamID);
                if (existing.success && existing.entry) {
                    return { success: true, record: existing.entry, pbId: existing.entry.id };
                }
            }
            return { success: false, error: error.message };
        }
    }

    /**
     * Retrieves a game entry by Steam ID
     * @param {string} steamID - The Steam ID to look up
     * @returns {Promise<Object>} - Result object with success boolean, optional entry, and optional error
     * Requirements: 10.1, 10.2
     */
    async getGameEntry(steamID) {
        try {
            const record = await this.pb.collection(this.collection)
                .getFirstListItem(`steamID="${steamID}"`, {
                    $autoCancel: false
                });

            return {
                success: true,
                entry: this.recordToGameEntry(record)
            };
        } catch (error) {
            // 404 means entry not found, which is not an error condition
            if (error.status === 404) {
                return { success: true, entry: null };
            }
            return { success: false, error: error.message };
        }
    }

    /**
     * Updates an existing game entry (non-user fields like apiInfo, playedTogether, etc.)
     * @param {string} steamID - The Steam ID of the entry to update
     * @param {Object} updates - Partial update with field names matching PB schema
     * @returns {Promise<Object>} - Result object with success boolean and optional error
     * Requirements: 10.1, 10.2
     */
    async updateGameEntry(steamID, updates) {
        try {
            // First, find the existing record
            const existing = await this.pb.collection(this.collection)
                .getFirstListItem(`steamID="${steamID}"`, {
                    $autoCancel: false
                });

            const toISO = (val) => {
                if (!val) return null;
                if (typeof val === 'string') return val;
                return val.toISOString();
            };

            // Process updates — convert Date objects to ISO strings.
            const processedUpdates = {};
            for (const [key, value] of Object.entries(updates)) {
                if (value instanceof Date) {
                    processedUpdates[key] = toISO(value);
                } else {
                    processedUpdates[key] = value;
                }
            }

            const record = await this.proxy.collection(this.collection).update(existing.id, processedUpdates);

            return { success: true };
        } catch (error) {
            if (error.status === 404) {
                return {
                    success: false,
                    error: 'Game entry not found'
                };
            }
            return { success: false, error: error.message };
        }
    }

    /**
     * Updates Steam API fields for an existing game entry
     * @param {string} steamID - The Steam ID of the entry to update
     * @param {Object} steamData - Steam API data with fields to update
     * @returns {Promise<Object>} - Result object with success boolean and optional error
     */
    async updateSteamFields(steamID, steamData) {
        try {
            // First, find the existing record
            const existing = await this.pb.collection(this.collection)
                .getFirstListItem(`steamID="${steamID}"`, {
                    $autoCancel: false
                });

            // Update only Steam API fields (preserve user metadata)
            const record = await this.proxy.collection(this.collection).update(existing.id, {
                title: steamData.title,
                imageURL: steamData.imageURL,
                genres: steamData.genres,
                storeURL: steamData.storeURL,
                creator: steamData.creator,
                releaseDate: steamData.releaseDate instanceof Date
                    ? steamData.releaseDate.toISOString()
                    : steamData.releaseDate || null,
                isDLC: steamData.isDLC
            });

            return { success: true };
        } catch (error) {
            if (error.status === 404) {
                return {
                    success: false,
                    error: 'Game entry not found'
                };
            }
            return { success: false, error: error.message };
        }
    }

    /**
     * Checks if a game entry exists for the given Steam ID
     * @param {string} steamID - The Steam ID to check
     * @returns {Promise<boolean>} - True if entry exists, false otherwise
     * Requirements: 10.1, 10.2
     */
    async gameEntryExists(steamID) {
        try {
            await this.pb.collection(this.collection)
                .getFirstListItem(`steamID="${steamID}"`, {
                    $autoCancel: false
                });
            return true;
        } catch (error) {
            return false;
        }
    }

    /**
     * Retrieves all game entries from the database
     * @returns {Promise<Object>} - Result object with success boolean, optional entries array, and optional error
     * Requirements: 10.1, 10.2
     */
    async getAllGameEntries() {
        try {
            const records = await this.pb.collection(this.collection)
                .getFullList({
                    sort: '-created',
                    // Disable auto-cancellation for this request
                    $autoCancel: false
                });

            const entries = records.map(r => this.recordToGameEntry(r));

            return { success: true, entries };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async deleteGameEntry(steamID) {
        try {
            const record = await this.pb.collection(this.collection)
                .getFirstListItem(`steamID="${steamID}"`, { $autoCancel: false });
            await this.proxy.collection(this.collection).delete(record.id);
            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    /**
     * Converts a PocketBase record to a GameEntry instance.
     * @param {Object} record - PocketBase record
     * @returns {GameEntry} - GameEntry instance
     * Requirements: 10.3, 10.4
     */
    recordToGameEntry(record) {
        const entry = new GameEntry({
            steamID: record.steamID,
            source: record.source || 'steam',
            title: record.title,
            imageURL: record.imageURL,
            genres: record.genres,
            storeURL: record.storeURL,
            creator: record.creator,
            releaseDate: new Date(record.releaseDate),
            isDLC: record.isDLC,
        });

        // Preserve PocketBase record ID for join table merging
        entry.id = record.id;

        // Non-user fields
        entry.playedTogether = record.playedTogether ?? false;
        entry.apiInfo = record.apiInfo ?? null;

        return entry;
    }

}

export default DatabaseRepository;

